<template>
  <div>
    <ts-modal
      ref="modal"
      :modal-title="modalTitle"
      :modal-class="modalClass"
      :keyboard="false"
    >
      <template #modal-content>
        <div
          v-if="error"
          class="alert alert-danger"
          role="alert"
        >
          {{ error }}
        </div>
        <div v-if="showSearchField">
          <div class="row search-row">
            <div class="col mb-2 search-candidate">
              <label class="form-label">{{ searchPromptTitle }}</label>
              <select
                id="search-candidate"
                ref="searchCandidateField"
                class="form-select form-select-sm autocomplete"
                :data-autocomplete-url="searchUrl"
                data-placeholder="Search for existing candidate"
                data-allow-clear="true"
              />
            </div>
            <div class="col col-auto col-mid text-secondary">
              <label class="mb-2">&mdash; OR &mdash;</label>
            </div>
            <div class="col col-auto col-mid">
              <button
                class="btn btn-sm btn-primary"
                @click="showNewForm"
              >
                {{ searchNewButtonTitle }}
              </button>
            </div>
          </div>
          <loading-indicator
            v-if="isLoadingSearchResults"
            message="Loading ..."
            :vertical="true"
          />
          <div
            v-if="showSearchResults"
            class="table-responsive"
          >
            <table class="table table-striped table-bordered table-hover table-sm import-table">
              <thead>
                <tr>
                  <th class="tight" />
                  <th
                    v-for="column in searchResultsColumns"
                    :key="column.id"
                  >
                    {{ column.title }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="(candidate, idx) in searchResultsItems || []"
                  :key="`candidate-${idx}`"
                >
                  <tr>
                    <td class="tight">
                      <button
                        v-if="simpleAddFromSearch || canAddCandidate || canAddTrainer"
                        type="button"
                        class="btn btn-sm btn-success"
                        @click.prevent="addCandidateToEvent(candidate)"
                      >
                        Add
                      </button>
                    </td>
                    <td
                      v-for="column in searchResultsColumns"
                      :key="column.id"
                    >
                      {{ candidate.attributes[column.id] }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
        <div
          v-if="showForm"
          v-html="formHTML"
        />
      </template>
      <template #modal-footer>
        <button
          v-if="showDelete"
          type="button"
          class="btn btn-sm btn-danger"
          @click="deleteCandidate"
        >
          Delete
        </button>
        <button
          v-if="showCancelParticipation"
          type="button"
          class="btn btn-sm btn-danger"
          @click="cancelParticipation"
        >
          Cancel participation
        </button>
        <button
          type="button"
          class="btn btn-sm btn-secondary"
          data-bs-dismiss="modal"
        >
          Close
        </button>
        <button
          v-if="showForm"
          type="button"
          class="btn btn-sm btn-primary"
          :disabled="!canSubmit"
          @click="submitForm"
        >
          Save changes
        </button>
      </template>
      <template #modal-footer-after>
        <loading-indicator
          v-if="isLoading"
        />
      </template>
    </ts-modal>
    <teleport
      to="#vue-portal-target"
      append
    >
      <cancel-participation-modal
        ref="cancelParticipationModal"
        :cancel-url="cancelParticipationUrl"
        :cancellation-reasons="cancellationReasons"
        @participation-canceled="participationCanceled"
      />
      <candidate-event-modal
        v-if="canAddCandidate"
        ref="eventCandidateModal"
        modal-title="Add candidate to event"
        :save-url="createCandidateUrlProp"
      />
      <trainer-event-modal
        v-if="canAddTrainer"
        ref="eventTrainerModal"
        :save-url="createInstructorUrlProp"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, type PropType } from "vue"
import $ from 'jquery'

import axios from "axios"
import Rails from "@rails/ujs"
import LoadingIndicator from "./loading-indicator.vue"
import TsModal from './ts-modal.vue'

import CandidateModalEvents from "./candidate-modal-events.vue"
import CandidateEventModal from "./candidate-event-modal.vue"
import TrainerEventModal from "./trainer-event-modal.vue"
import { EventCandidateStatus } from "./../event-candidate-status"
import CancelParticipationModal from "./cancel-participation-modal.vue"

import { initializeAutocompleteField, initializeFormFields } from "../site-utils"
import { isNonEmptyString, isPresent, tsInitVue, type MountedApp } from "../js-utils"
import type { CancellationReason } from "./types"

interface SearchResultColumn {
  id: string;
  title: string;
}

interface SearchResultCandidate {
  id: number;
  attributes: {
    [index: string]: string;
  };
}

interface CandidateModalEvent {
  id: number;
  name?: string;
  languages?: unknown[];
  addCandidateToEventUrl?: string | null;
}

const props = defineProps({
  modalTitle: { default: '', type: String },
  searchPromptTitle: { default: 'Find candidate', type: String },
  searchNewButtonTitle: { default: 'Create new candidate', type: String },
  showSearch: { default: false, type: Boolean },
  searchUrl: { default: null, type: String as PropType<string | null> },
  event: { default: null, type: Object as PropType<CandidateModalEvent | null> },
  typeId: { default: 1, type: Number },
  createInstructorUrl: { default: null, type: String as PropType<string | null> },

  simpleAddFromSearch: { default: false, type: Boolean }
})

const emit = defineEmits<{
  success: [candidate?: any];
  "add-candidate-to-event": [candidate: SearchResultCandidate];
}>()

const modal = useTemplateRef<InstanceType<typeof TsModal>>('modal')
const searchCandidateField = useTemplateRef<HTMLSelectElement>('searchCandidateField')
const cancelParticipationModal = useTemplateRef<InstanceType<typeof CancelParticipationModal>>('cancelParticipationModal')
const eventCandidateModal = useTemplateRef<InstanceType<typeof CandidateEventModal>>('eventCandidateModal')
const eventTrainerModal = useTemplateRef<InstanceType<typeof TrainerEventModal>>('eventTrainerModal')

const isLoading = ref(false)
const error = ref<string | null>(null)
const formHTML = ref<string | null>(null)
const formEl = ref<HTMLElement | null>(null)
const eventsComponent = ref<MountedApp | null>(null)
const events = ref<any[] | null>(null)
const eventId = ref<number | null>(null)
const addToEvent = ref<any>(null)
const formUrl = ref<string | null>(null)
const hideSearchField = ref(false)
const isLoadingSearchResults = ref(false)
const searchResultsItems = ref<SearchResultCandidate[] | null>(null)
const searchResultsColumns = ref<SearchResultColumn[]>([])
const deleteUrl = ref<string | null>(null)
const cancelParticipationUrl = ref<string | null>(null)
const cancellationReasons = ref<CancellationReason[]>([])

const canSubmit = computed((): boolean => {
  return (!isLoading.value && !error.value && formEl.value && true) || false
})

const showForm = computed((): boolean => {
  return !!formHTML.value
})

const withSearchField = computed((): boolean => {
  return props.searchUrl && props.showSearch || false
})

const modalClass = computed((): string => {
  return ['modal-form candidate-modal', withSearchField.value ? 'candidate-modal-wide' : ''].filter(Boolean).join(' ')
})

const showSearchField = computed((): boolean => {
  return withSearchField.value && !hideSearchField.value
})

const showSearchResults = computed((): boolean => {
  return !isLoadingSearchResults.value && Array.isArray(searchResultsItems.value)
})

const showDelete = computed((): boolean => {
  return isNonEmptyString(deleteUrl.value)
})

const showCancelParticipation = computed((): boolean => {
  return typeof cancelParticipationUrl.value === 'string' && isPresent(cancelParticipationUrl.value)
})

const createCandidateUrl = computed((): string | null => {
  return props.event?.addCandidateToEventUrl || null
})

const canAddCandidate = computed((): boolean => {
  return isNonEmptyString(createCandidateUrl.value)
})

const createCandidateUrlProp = computed((): string | undefined => {
  return createCandidateUrl.value ?? undefined
})

const canAddTrainer = computed((): boolean => {
  return isNonEmptyString(props.createInstructorUrl)
})

const createInstructorUrlProp = computed((): string => {
  return props.createInstructorUrl ?? ''
})

const showLoadingIndicator = (show: boolean): void => {
  isLoading.value = show
}

const removeForm = (): void => {
  if (eventsComponent.value) {
    eventsComponent.value?.app?.unmount()
    eventsComponent.value = null
  }
  events.value = null
  formEl.value = null
  formHTML.value = null
}

const loadCandidates = (url: string): void => {
  isLoadingSearchResults.value = true

  axios.get(url, {
    headers: {
      'Accept': 'application/vnd.api+json'
    }
  })
    .then((response) => {
      isLoadingSearchResults.value = false
      if (Array.isArray(response.data.meta.columns)) {
        searchResultsColumns.value = response.data.meta.columns
      } else {
        searchResultsColumns.value = []
      }

      searchResultsItems.value = response.data.data
    })
    .catch(() => {
      isLoadingSearchResults.value = false
    })
}

const attachToFormEvents = (): void => {
  const modalEl = modal.value?.modalEl
  if (modalEl) {
    formEl.value = modalEl.querySelector(':scope form[ref="inputForm"]')

    if (formEl.value && formEl.value.getAttribute('data-form-initialized') !== 'true') {
      // need to set initialized attribute, so attachment to events
      // does not happen multiple times if formHTML does not changes
      // between the requests
      formEl.value.setAttribute('data-form-initialized', 'true')

      // force request to be json
      formEl.value.setAttribute('data-type', 'json')

      // initialize form
      initializeFormFields(formEl.value)

      // attach to events
      formEl.value.addEventListener('ajax:beforeSend', () => {
        showLoadingIndicator(true)
        return true
      })
      formEl.value.addEventListener('ajax:success', (e) => {
        const [data] = (e as any).detail
        setupForm(data)
        showLoadingIndicator(false)
      })
      formEl.value.addEventListener('ajax:error', (e) => {
        const [, status, xhr] = (e as any).detail
        alert(`Error: ${xhr.status} - ${status}`)
        showLoadingIndicator(false)
      })

      // mount component inside form
      const eventsEl = formEl.value.querySelector(':scope [ref="events"]')
      if (eventsEl instanceof HTMLElement) {
        const modelEventsApp: MountedApp = {}
        const component = tsInitVue(CandidateModalEvents, eventsEl, {
          eventsUrl: eventsEl.dataset.eventsUrl,
          cancellationReasons: cancellationReasons.value,
        }, modelEventsApp)
        eventsComponent.value = modelEventsApp
        component.setEvents(events.value)
        component.setAddToEvent(addToEvent.value)
      }
    }
  }
}

const setupForm = (responseData: any): void => {
  removeForm()
  const status = responseData.status
  if (typeof status !== 'undefined' && status != null) {
    if (status === 'success') {
      // form operation succeeded
      emit('success', responseData.candidate)
      return
    }
  }

  let html = responseData.html
  if (typeof html === 'undefined' || html == null) {
    html = null
  }

  let responseEvents = responseData.events
  if (typeof responseEvents === 'undefined' || responseEvents == null) {
    responseEvents = []
  }
  events.value = responseEvents

  let responseAddToEvent = responseData.add_to_event
  if (typeof responseAddToEvent === 'undefined' || responseAddToEvent == null) {
    responseAddToEvent = null
  }
  addToEvent.value = responseAddToEvent

  const responseDeleteUrl = responseData.deleteUrl
  if (isNonEmptyString(responseDeleteUrl)) {
    deleteUrl.value = responseDeleteUrl
  } else {
    deleteUrl.value = null
  }

  const cancelUrl = responseData.cancelUrl
  if (isNonEmptyString(cancelUrl)) {
    cancelParticipationUrl.value = cancelUrl
  }

  const responseCancellationReasons = responseData.cancellationReasons
  if (Array.isArray(responseCancellationReasons)) {
    cancellationReasons.value = responseCancellationReasons
  }

  // to ensure that form html is updated correctly
  // perform form html manipulation on $nextTick
  // because html has just been unset at the begining
  // of this method and vue has not had time to celar form
  nextTick(() => {
    formHTML.value = html
    if (formHTML.value) {
      nextTick(() => {
        attachToFormEvents()
      })
    }
  })
}

const loadForm = (): void => {
  if (showSearchField.value) {
    // setup search candidate field
    const searchCandidateEl = searchCandidateField.value
    const dialogEl = modal.value?.modalEl?.querySelector<HTMLElement>(':scope .modal-dialog')
    if (searchCandidateEl && dialogEl) {
      initializeAutocompleteField(searchCandidateEl, dialogEl, () => {
        $(searchCandidateEl).on('select2:select', (e) => {
          if (e.params.data.id) {
            loadCandidates((e.params.data as any).listUrl)
          }
        })
      })
    }
    return
  }

  error.value = null
  removeForm()

  showLoadingIndicator(true)
  let reqParams = undefined
  if (eventId.value) {
    reqParams = {
      params: {
        event_id: eventId.value
      }
    }
  }
  if (isNonEmptyString(formUrl.value)) {
    axios.get(formUrl.value, reqParams)
      .then((response) => {
        showLoadingIndicator(false)
        setupForm(response.data)
      })
      .catch((requestError) => {
        error.value = requestError.message
        showLoadingIndicator(false)
      })
  }
}

const showModal = (newFormUrl: string, newEventId: number | null): void => {
  if (!modal.value!.isOpen) {
    // reset modal state
    isLoadingSearchResults.value = false
    searchResultsItems.value = null
    hideSearchField.value = false
    formUrl.value = newFormUrl
    formHTML.value = null
    cancellationReasons.value = []

    eventId.value = newEventId

    // show modal on next render
    nextTick(() => {
      // load form's html
      loadForm()
      modal.value?.show()
    })
  }
}

const hideModal = (): void => {
  modal.value?.hide()
}

const submitForm = (): void => {
  if (formEl.value) {
    Rails.fire(formEl.value, 'submit')
  } else {
    alert('Form not initialized')
  }
}

const showNewForm = (): void => {
  hideSearchField.value = true
  loadForm()
}

const deleteCandidate = (): void => {
  if (!isNonEmptyString(deleteUrl.value)) {
    return
  }

  const eventData = {} as { [index: number]: any };
  let idx = 0
  if (Array.isArray(events.value)) {
    for (const event of events.value) {
      if (event.statusId == EventCandidateStatus.canceled || event.statusId == EventCandidateStatus.deleted) {
        eventData[idx] = {
          id: event.id,
          event_id: event.eventId,
          _cancel: '1'
        }
      }
      idx += 1
    }
  }

  if (confirm('Delete candidate?')) {
    showLoadingIndicator(true)
    axios.delete(deleteUrl.value, {
      data: {
        events: eventData
      },
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken()
      }
    })
      .then((response) => {
        showLoadingIndicator(false)
        if (response.data && response.data.error) {
          alert(response.data.error)
        } else {
          emit('success')
        }
      })
      .catch(() => {
        // this.error = error.message
        showLoadingIndicator(false)
      })
  }
}

const cancelParticipation = (): void => {
  cancelParticipationModal.value?.showModal()
}

const participationCanceled = (): void => {
  cancelParticipationModal.value?.hideModal()
  emit('success')
}

const addCandidateToEvent = (candidate: SearchResultCandidate): void => {
  if (props.simpleAddFromSearch) {
    emit('add-candidate-to-event', candidate)
    return
  }

  const candidateId = candidate.id

  if (props.typeId == 1) {
    const event = {
      id: null,
      eventId: props.event ? props.event.id : null,
      eventName: props.event ? props.event.name : null,
      eventLanguages: props.event ? props.event.languages : [],
      candidateId: candidateId,
      rm: false,
      priority: '',
      statusId: EventCandidateStatus.new,
      editable: true
    }
    eventCandidateModal.value?.showModal(event as any)
  } else if (props.typeId == 2) {
    const event = {
      eventId: props.event ? props.event.id : null,
      eventName: props.event ? props.event.name : null,
      candidateId: candidateId,
      isActivityCoordinator: false,
      isStaff: false,
    }

    eventTrainerModal.value?.showModal(event as any)
  }
}

defineExpose({
  showModal,
  hideModal,
})
</script>
